import type { IsoDateTime, UserSummary, Uuid } from '@/shared/types/domain'

/**
 * Documentos: markdown propio + links (plan 2.2).
 *
 * Un mismo módulo cubre los dos casos con `kind`. Un CHECK de la base exige que
 * exactamente uno de `content_md` / `external_url` esté presente, así que la UI
 * no ofrece nunca los dos a la vez.
 */
export type DocumentKind = 'markdown' | 'link'

/** Todo nace `internal`. Hacerse visible al cliente es un acto explícito. */
export type DocumentVisibility = 'internal' | 'client'

export interface DocumentSummary {
  id: Uuid
  company_id: Uuid
  project_id: Uuid | null
  title: string
  doc_type: string
  kind: DocumentKind
  external_url: string | null
  visibility: DocumentVisibility
  /** Versionado append-only: el número de la última versión guardada. */
  current_version: number
  updated_at: IsoDateTime
  updated_by: UserSummary | null
}

export interface DocumentDetail extends DocumentSummary {
  content_md: string | null
}

/**
 * Filtros de `GET /documents`. El index signature no es decorativo: la factory
 * de query keys y `toSearchParams` reciben `Record<string, unknown>`, y sin el
 * index un `interface` no es asignable (los `type` alias sí).
 */
export type DocumentFilters = {
  company_id?: string
  project_id?: string
  doc_type?: string
}

export interface DocumentCreateInput {
  company_id: Uuid
  project_id?: Uuid | null
  title: string
  doc_type: string
  kind: DocumentKind
  content_md?: string | null
  external_url?: string | null
  visibility?: DocumentVisibility
}

export interface DocumentPatchInput {
  title?: string
  content_md?: string | null
  external_url?: string | null
  visibility?: DocumentVisibility
  /**
   * La versión sobre la que se editó. Si el servidor tiene otra, contesta 409 y
   * la UI muestra las dos. Sin esto, el último en guardar pisa al otro en
   * silencio, que es la forma más cara de perder media hora de escritura.
   */
  expected_version?: number
}

/**
 * Los tipos de documento los escribimos nosotros, así que se traducen: el mapa
 * guarda la CLAVE del catálogo, no el texto. El orden es el que ve el `select`
 * del alta.
 */
export const DOC_TYPE_KEY: Record<string, string> = {
  brief: 'docs.docType.brief',
  spec: 'docs.docType.spec',
  design: 'docs.docType.design',
  runbook: 'docs.docType.runbook',
  onboarding: 'docs.docType.onboarding',
  note: 'docs.docType.note',
  other: 'docs.docType.other',
}

/**
 * Un `doc_type` que no conocemos viaja tal cual: lo escribió alguien del otro
 * lado y traducirlo sería inventarle un nombre.
 */
export function docTypeLabel(t: (key: string) => string, docType: string): string {
  const key = DOC_TYPE_KEY[docType]
  return key ? t(key) : docType
}
